import Branch, { DeepLinkData } from 'branch-sdk';
import { buildDesktopReferralUrl, withDesktopReferralDestination } from './referralDesktop';

const generatedLinks = new Set<string>();
const shortLinks = new Map<string, Promise<string>>();

const generateDynamicLink = async (urlSuffixName: string, options?: {
    withoutAlias?: boolean; mobileData?: Record<string, string>;
}): Promise<string | null> => {
    try {
        const API_KEY = process.env.REACT_APP_BRANCH_IO_API_KEY;
        const androidDeeplink = process.env.REACT_APP_ANDROID_DEEPLINK_PATH ?? 'quickasyst://';
        const iosDeeplink = process.env.REACT_APP_IOS_DEEPLINK_PATH ?? 'quickasyst://';
        const androidUrl = process.env.REACT_APP_ANDROID_URL ?? '';
        const iosUrl = process.env.REACT_APP_IOS_URL ?? '';
        if (!API_KEY) {
            throw new Error('Branch.io API key is not defined.');
        }
        if (!urlSuffixName || !urlSuffixName.trim()) {
            throw new Error('Please enter a URL suffix.');
        }
        const desktopUrl = buildDesktopReferralUrl(urlSuffixName);
        Branch.init(API_KEY);
        const deepLinkData: DeepLinkData & {alias?: string} = {
            tags: ['QuickAsyst Referral Link'],
            feature: 'share',
            stage: 'new user',
            ...(!options?.withoutAlias && { alias: urlSuffixName }),
            data: {
                $web_only: false,
                $desktop_url: desktopUrl,
                $desktop_deepview: 'false',
                $desktop_web_only: true,
                $android_deeplink_path: androidDeeplink,
                $ios_deeplink_path: iosDeeplink,
                ...(androidUrl && { $android_url: androidUrl }),
                ...(iosUrl && { $ios_url: iosUrl }),
                ...options?.mobileData,
                title: 'QuickAsyst Referral Link',
                contentDescription: `Deep link for user`,
                qac: urlSuffixName,
                customMetadata: {
                    referralCode: urlSuffixName,
                }
            }
        };        

        const generateLink = await new Promise<string | null>((resolve, reject) => {
            Branch.link(deepLinkData, (err: any, link: any) => {
                if (err) {
                    console.error('Error generating link', err);
                    reject(err);
                    return;
                }

                resolve(link);
            });
        });

        if (generateLink) generatedLinks.add(generateLink);
        return generateLink;
    } catch (error: any) {
        const errorStr = error?.message || error?.toString();

        if (errorStr.includes('Link alias conflict')) {
            throw new Error('URL Suffix already exists');
        }

        console.error('Error generating dynamic link:', error);
        throw error;
    }
};

/** Store routing inside Branch metadata, rather than appending it to the shared URL. */
export async function shortenAffiliateLink(link: string, code?: string): Promise<string> {
    if (!link || generatedLinks.has(link)) return link;
    let url: URL;
    try { url = new URL(link); } catch { return link; }
    if (!['quickasyst.app.link', 'quickasyst.test-app.link', 'quickasyst.app', 'quickasyst.page.link'].includes(url.hostname)) return link;
    if (!['https:', 'http:'].includes(url.protocol)) return link;
    const referralCode = code?.trim() || url.searchParams.get('qac') || url.searchParams.get('referralCode') ||
        (url.hostname === 'quickasyst.app' ? url.pathname.replace(/^\/ref\//, '') : url.pathname.slice(1));
    if (!referralCode) throw new Error('Referral code is missing.');
    const repaired = withDesktopReferralDestination(link, referralCode);
    const cacheKey = `qa-affiliate-short-link:${process.env.REACT_APP_BRANCH_IO_API_KEY}:${buildDesktopReferralUrl(referralCode)}:${referralCode}:${repaired}`;
    try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached && /^https:\/\/quickasyst\.(test-app|app)\.link\/[^?#]+$/.test(cached)) return cached;
    } catch { /* Storage is optional; the in-memory cache still deduplicates requests. */ }
    const existing = shortLinks.get(cacheKey);
    if (existing) return existing;
    const mobileData: Record<string, string> = {};
    for (const key of ['$android_url', '$ios_url', '$android_deeplink_path', '$ios_deeplink_path']) {
        const value = url.searchParams.get(key);
        if (value) mobileData[key] = value;
    }
    const pending = generateDynamicLink(referralCode, { withoutAlias: true, mobileData }).then(shortLink => {
        if (!shortLink) throw new Error('Failed to generate short referral link.');
        try { sessionStorage.setItem(cacheKey, shortLink); } catch { /* Optional cache. */ }
        return shortLink;
    }).catch(error => { shortLinks.delete(cacheKey); throw error; });
    shortLinks.set(cacheKey, pending);
    return pending;
}

export default generateDynamicLink;
